/**
 * Mirrors apps/api/src/procurement/{requests,orders}.controller.ts and
 * packages/modules/procurement/src/presentation.ts. Real fields only — no
 * priority, source, related-project, tax, or payment-request fields exist
 * anywhere in this module; see the UI_REFERENCE_SOURCE comments on the
 * pages that read this file for what the design board showed instead.
 */

export type PurchaseRequestStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'cancelled';

/**
 * `apps/api/src/procurement/requests.controller.ts`'s `toRequestResponse`
 * (used for both the list and the detail response) never returns the raw
 * `status` enum — only the real `statusLabel` Persian string
 * (`PURCHASE_REQUEST_STATUS_LABELS` in packages/modules/procurement/src/
 * labels.ts). This reverses that same real, exact label map back to the
 * enum key so pages can drive badge tone and action-button visibility off
 * it — not a guess, the exact inverse of a real backend-defined dictionary.
 */
const REQUEST_STATUS_BY_LABEL: Readonly<Record<string, PurchaseRequestStatus>> = {
  پیش‌نویس: 'draft',
  ارسال‌شده: 'submitted',
  تأیید‌شده: 'approved',
  رد‌شده: 'rejected',
  لغو‌شده: 'cancelled',
};

export function requestStatusOf(statusLabel: string): PurchaseRequestStatus | undefined {
  return REQUEST_STATUS_BY_LABEL[statusLabel];
}

export type PurchaseOrderStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'in_transit'
  | 'partially_received'
  | 'received'
  | 'cancelled';

/** Same reasoning as `requestStatusOf` — `toOrderResponse` never returns
 * the raw `status` either, only `statusLabel`. */
const ORDER_STATUS_BY_LABEL: Readonly<Record<string, PurchaseOrderStatus>> = {
  پیش‌نویس: 'draft',
  ارسال‌شده: 'submitted',
  تأیید‌شده: 'approved',
  'در راه': 'in_transit',
  'دریافت جزئی': 'partially_received',
  'دریافت کامل': 'received',
  لغو‌شده: 'cancelled',
};

export function orderStatusOf(statusLabel: string): PurchaseOrderStatus | undefined {
  return ORDER_STATUS_BY_LABEL[statusLabel];
}

export type ApprovalDecision = 'approved' | 'rejected';

export interface ApprovalEvent {
  readonly entityType: 'purchase_request' | 'purchase_order';
  readonly entityRef: string;
  readonly decision: ApprovalDecision;
  readonly decidedAt: string;
  readonly note: string;
}

export interface PurchaseRequestLine {
  readonly rowNumber: number;
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly uomRef: string;
  readonly estimatedUnitPriceRial: string | null;
  readonly notes: string;
}

export interface PurchaseRequest {
  readonly ref: string;
  readonly title: string;
  readonly purpose: string;
  readonly requestedDate: string;
  readonly neededByDate: string | null;
  readonly destinationWarehouseRef?: string | null;
  readonly destinationWarehouseName?: string | null;
  readonly projectRef?: string | null;
  readonly projectName?: string | null;
  readonly costCenterRef?: string | null;
  readonly costCenterName?: string | null;
  readonly statusLabel: string;
  readonly notes: string;
  readonly lines: readonly PurchaseRequestLine[];
}

export interface PurchaseRequestDetail extends PurchaseRequest {
  readonly approvals: readonly ApprovalEvent[];
}

export interface PurchaseRequestListResponse {
  readonly requests: readonly PurchaseRequest[];
}

export interface PurchaseRequestLineDraft {
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly uomRef: string;
  readonly estimatedUnitPriceRial: string;
  readonly notes: string;
}

export interface PurchaseOrderLine {
  readonly rowNumber: number;
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly receivedQuantity: string;
  readonly uomRef: string;
  readonly unitPriceRial: string;
  readonly lineTotalRial: string;
  readonly notes: string;
}

export interface PurchaseOrder {
  readonly ref: string;
  readonly supplierPartyRef: string;
  readonly requestRef: string | null;
  readonly projectRef?: string | null;
  readonly projectName?: string | null;
  readonly orderDate: string;
  readonly expectedDate: string | null;
  readonly trackingNumber?: string | null;
  readonly carrierName?: string | null;
  readonly shippedAt?: string | null;
  readonly status?: PurchaseOrderStatus;
  readonly statusLabel: string;
  readonly notes: string;
  readonly subtotalRial: string;
  readonly totalRial: string;
  readonly lines: readonly PurchaseOrderLine[];
}

export interface PurchaseOrderDetail extends PurchaseOrder {
  readonly approvals: readonly ApprovalEvent[];
}

export interface PurchaseOrderListResponse {
  readonly orders: readonly PurchaseOrder[];
}

export interface PurchaseOrderLineDraft {
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly uomRef: string;
  readonly unitPriceRial: string;
  readonly notes: string;
}

export interface ReceiptLink {
  readonly orderRef: string;
  readonly orderLineRowNumber: number;
  readonly movementNumber: string;
  readonly quantity: string;
  readonly receivedAt: string;
}

export interface OrderReceiptsResponse {
  readonly receipts: readonly ReceiptLink[];
}

export type PurchaseOrderChargeType = 'freight' | 'insurance' | 'customs' | 'handling' | 'other';

export type LandedCostAllocationMethod = 'value' | 'quantity' | 'equal';

export const CHARGE_TYPE_LABELS: Record<PurchaseOrderChargeType, string> = {
  freight: 'کرایه حمل',
  insurance: 'بیمه',
  customs: 'عوارض و گمرک',
  handling: 'تخلیه و بارگیری',
  other: 'سایر هزینه‌ها',
};

export const ALLOCATION_METHOD_LABELS: Record<LandedCostAllocationMethod, string> = {
  value: 'بر اساس ارزش ریالی',
  quantity: 'بر اساس تعداد / مقدار',
  equal: 'تسهیم مساوی بین اقلام',
};

export interface PurchaseOrderCharge {
  readonly chargeId: string;
  readonly orderRef: string;
  readonly chargeType: PurchaseOrderChargeType;
  readonly chargeTypeLabel: string;
  readonly title: string;
  readonly amountRial: string;
  readonly supplierPartyRef: string | null;
  readonly supplierPartyName: string | null;
  readonly allocationMethod: LandedCostAllocationMethod;
  readonly allocationMethodLabel: string;
  readonly notes: string;
  readonly createdAt: string;
}

export interface LandedCostLine {
  readonly rowNumber: number;
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPriceRial: string;
  readonly lineTotalRial: string;
  readonly allocatedChargesRial: string;
  readonly landedTotalRial: string;
  readonly landedUnitCostRial: string;
}

export interface PurchaseOrderLandedCostsResponse {
  readonly orderRef: string;
  readonly orderTotalRial: string;
  readonly totalChargesRial: string;
  readonly grandTotalRial: string;
  readonly charges: readonly PurchaseOrderCharge[];
  readonly lines: readonly LandedCostLine[];
}

export interface OrderChargesResponse {
  readonly charges: readonly PurchaseOrderCharge[];
}

export interface ProcurementAttachment {
  readonly attachmentId: string;
  readonly entityType: 'request' | 'order';
  readonly entityRef: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly sha256: string;
  readonly uploadedBy: string;
  readonly createdAt: string;
}

export interface ProcurementAttachmentListResponse {
  readonly items: readonly ProcurementAttachment[];
  readonly maxBytes: number;
}
